const getNetRecurringBenefitChangeCategoryL3Plus = (initiative) => {
  // Helper function to sum annualized plan values, handling both number and array types
  const sumAnnualizedPlan = (value) => {
      if (typeof value === 'number') {
          return value;
      }
      if (Array.isArray(value)) {
          return value.reduce((sum, val) => sum + (val || 0), 0);
      }
      return 0;
  };

  // This function calculates the net recurring benefit for a given initiative
  // considering its stage and impacts.
  const calculateNetRecurringBenefitForStages = (init, stages) => {
      const impacts = init.impact || [];

      if (!impacts || impacts.length === 0) {
          return NaN; // Indicate no impact
      }

      let recurringBenefit = 0;
      let negativeRecurringImpact = 0;

      // Determine if the initiative's stage is within the specified stages
      let isInTargetStages = false;
      if (init.stage) {
          if (Array.isArray(init.stage)) {
              isInTargetStages = init.stage.some((stage) => stages.includes(stage));
          } else if (typeof init.stage === 'string') {
              isInTargetStages = stages.includes(init.stage);
          }
      }

      if (isInTargetStages) {
          recurringBenefit = impacts
              .filter((impact) => impact.frequencyAndDirection === 'Recurring benefits')
              .reduce((sum, impact) => sum + sumAnnualizedPlan(impact.annualizedValues?.plan), 0);

          negativeRecurringImpact = impacts
              .filter((impact) => impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)')
              .reduce((sum, impact) => sum + sumAnnualizedPlan(impact.annualizedValues?.plan), 0);
      }

      return recurringBenefit - negativeRecurringImpact;
  };

  // Calculate L3+ Net Recurring Benefit for the current initiative state
  const l3PlusStages = [
      'L3', 'L4', 'L5',
      'Submitted for L3 Approval', 'Submitted for L4 Approval', 'Submitted for L5 Approval'
  ];
  const currentValue = calculateNetRecurringBenefitForStages(initiative, l3PlusStages);

  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const history = initiative.history || [];
  const impactUpdateHistory = history.filter(
      (entry) =>
          entry.action &&
          entry.action.includes('Impact number') &&
          entry.action.includes('has been updated.')
  );

  // Filter for impact updates that occurred *before* one week ago
  const oldImpactHistories = impactUpdateHistory.filter(
      (entry) => new Date(entry.timestamp) < oneWeekAgo
  );

  // Sort in descending order to easily find the most recent update before one week ago
  oldImpactHistories.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  let lastWeekValue = NaN; // Initialize to NaN, similar to how calculateNetRecurringBenefitForStages might return it

  // Find the net recurring benefit from the most recent historical entry before 'oneWeekAgo'
  if (oldImpactHistories.length > 0) {
      const latestRelevantEntry = oldImpactHistories[0];
      let historicalNetBenefit = 0;

      // The 'changes.impact' can be an array of impact objects, so we need to iterate
      // Assuming 'new' contains the state of the impacts *after* that historical update
      const impactsAfterHistoricalUpdate = latestRelevantEntry.changes?.impact?.new || [];

      impactsAfterHistoricalUpdate.forEach(impact => {
          if (impact.frequencyAndDirection && impact.frequencyAndDirection.startsWith('Recurring')) {
              const amount = sumAnnualizedPlan(impact.annualizedValues?.plan);
              if (impact.frequencyAndDirection.includes('Benefit')) {
                  historicalNetBenefit += amount;
              } else if (impact.frequencyAndDirection.includes('Negative')) {
                  historicalNetBenefit -= amount;
              }
          }
      });
      lastWeekValue = historicalNetBenefit;
  }

  // Determine the category based on current status and calculated values
  if (
      initiative.weeklyStatus === 'Cancelled' ||
      initiative.weeklyStatus === 'On Hold' || // Corrected to 'On Hold' for consistency
      initiative.weeklyStatus === 'Rejected'
  ) {
      return 'Cancelled/On hold';
  } else if (
      isNaN(currentValue) &&
      (isNaN(lastWeekValue) || lastWeekValue === 0) && // If no past impact or past impact was 0
      initiative.impact &&
      initiative.impact.length === 0 && // No current impacts
      (new Date(initiative.created) > oneWeekAgo) // Created within the last week
  ) {
      return 'New Initiative';
  } else if (isNaN(currentValue) || isNaN(lastWeekValue)) {
      // If either current or last week value is missing (NaN), or not a number, set as '<not set>'
      return '<not set>';
  } else if (currentValue > lastWeekValue) {
      return 'Increased value';
  } else if (currentValue < lastWeekValue) {
      return 'Decreased value';
  } else if (currentValue === lastWeekValue) {
      return 'Unchanged';
  }

  return '<not set>'; // Default fallback for any unhandled cases
};

export default getNetRecurringBenefitChangeCategoryL3Plus;